import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";

import { useTypedDispatch, useTypedSelector } from "../redux/store";
import tableColumns, { NEW_COLUMNS } from "../redux/reducers/TableColumns";
import Nullable from "../types/Nullable";
import TableSortMethod from "../types/SortMethod";
import TableWidths from "../types/TableWidths";
import StringWithSuggestions from "../types/StringWithSuggestions";
import { ColumnId, Tables } from "../constants/tables/types";
import tables from "../constants/tables";

function mergeVisibleColumnIds<T extends string>(
	saved: T[] | null | undefined,
	defaults: readonly T[],
): T[] {
	if (!saved?.length) {
		return cloneDeep(defaults) as T[];
	}

	return cloneDeep(saved) as T[];
}

/** Defaults that were raised later: `paymentType` was 60 px. */
const FORMER_DEFAULT_WIDTHS: Record<string, number> = {
	paymentType: 60,
};

function insertNewColumns(
	saved: string[],
	allIds: readonly string[],
	added: readonly string[],
): { columns: string[]; added: string[] } | null {
	const missing = NEW_COLUMNS.filter(
		(column) =>
			allIds.includes(column.id) &&
			!added.includes(column.id) &&
			!saved.includes(column.id),
	);
	if (!missing.length) return null;

	const columns = [...saved];
	missing.forEach((column) => {
		const afterIndex = column.after ? columns.indexOf(column.after) : -1;
		if (afterIndex >= 0) columns.splice(afterIndex + 1, 0, column.id);
		else columns.push(column.id);
	});

	return { columns, added: missing.map((column) => column.id) };
}

/**
 * Used to get columns with localized labels
 * @param tableId id of the table to be used
 * @returns columns of the table
 */
export function useColumns<TI extends keyof Tables>(tableId: TI) {
	const { t } = useTranslation();

	return useMemo(
		() =>
			tables[tableId].columns.map((column) => ({
				...column,
				label: t(column.label),
			})),
		[t, tableId],
	);
}

/**
 * @param saveSlot key under which table data will be stored
 * @param tableId id of the table to be used
 * @returns getter and setter of visible column ids of the table
 */
export function useVisibleColumns<
	TableId extends keyof Tables,
	// eslint-disable-next-line no-shadow
	ColumnIds extends Tables[TableId]["allIds"],
>(saveSlot: string, tableId: TableId, defaultIds?: ColumnIds) {
	const dispatch = useTypedDispatch();
	const nullableColumnIds = useTypedSelector(
		(state) => state.tableColumns.visible[saveSlot],
	) as Nullable<ColumnIds>;

	const columnIds = useMemo(
		() =>
			mergeVisibleColumnIds<ColumnIds[number]>(
				nullableColumnIds as ColumnIds | null | undefined,
				(defaultIds ||
					tables[tableId].defaultIds) as readonly ColumnIds[number][],
			) as ColumnIds,
		[defaultIds, nullableColumnIds, tableId],
	);
	const addedColumns = useTypedSelector(
		(state) => state.tableColumns.addedColumns?.[saveSlot],
	);

	useEffect(() => {
		if (!nullableColumnIds?.length) return;

		const result = insertNewColumns(
			nullableColumnIds as string[],
			tables[tableId].allIds as readonly string[],
			addedColumns ?? [],
		);
		if (result) {
			dispatch(
				tableColumns.actions.addNewColumns({
					uid: saveSlot,
					...result,
				}),
			);
		}
	}, [addedColumns, dispatch, nullableColumnIds, saveSlot, tableId]);

	const setColumnIds = useCallback(
		(newColumns: StringWithSuggestions<ColumnIds[number]>[]) => {
			dispatch(
				tableColumns.actions.setVisibleColumns({
					uid: saveSlot,
					columns: newColumns,
				}),
			);
		},
		[dispatch, saveSlot],
	);

	return useMemo(
		() => ({ columnIds, setColumnIds }),
		[columnIds, setColumnIds],
	);
}

/**
 * @param saveSlot key under which table data will be stored
 * @param tableId id of the table to be used
 * @returns getter and setter of a sort method of the table
 */
export function useSort<
	TableId extends keyof Tables,
	SortMethod extends TableSortMethod<ColumnId<TableId>>,
>(saveSlot: string, tableId: TableId) {
	const dispatch = useTypedDispatch();
	const nullableSortMethod = useTypedSelector(
		(state) => state.tableColumns.sortMethod[saveSlot],
	) as Nullable<SortMethod>;

	const sortMethod = useMemo(
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		() =>
			(nullableSortMethod ||
				(tables[tableId].initialSortMethod as SortMethod)) ??
			{},
		[nullableSortMethod, tableId],
	);
	const setSortMethod = useCallback(
		(
			newMethod: TableSortMethod<
				StringWithSuggestions<ColumnId<TableId>>
			>,
		) => {
			dispatch(
				tableColumns.actions.setSortMethod({
					uid: saveSlot,
					method: newMethod,
				}),
			);
		},
		[dispatch, saveSlot],
	);

	return useMemo(
		() => ({ sortMethod, setSortMethod }),
		[sortMethod, setSortMethod],
	);
}

/**
 * @param saveSlot key under which table data will be stored
 * @param tableId id of the table to be used
 * @returns getter and setter of column widths of the table
 */
export function useWidths<
	TableId extends keyof Tables,
	Widths extends Tables[TableId]["initialWidths"],
>(saveSlot: string, tableId: TableId) {
	const dispatch = useTypedDispatch();
	const nullableWidths = useTypedSelector(
		(state) => state.tableColumns.widths[saveSlot],
	) as TableWidths<ColumnId<TableId>>;

	const widths = useMemo<TableWidths<ColumnId<TableId>>>(() => {
		const initialWidths = (tables[tableId].initialWidths ?? {}) as Record<
			string,
			number
		>;
		const saved = { ...(nullableWidths ?? {}) } as Record<string, number>;

		// A width saved at a former default was never resized by hand:
		// show the current default instead.
		Object.entries(FORMER_DEFAULT_WIDTHS).forEach(([columnId, width]) => {
			if (saved[columnId] === width && initialWidths[columnId]) {
				saved[columnId] = initialWidths[columnId];
			}
		});

		return { ...initialWidths, ...saved } as TableWidths<ColumnId<TableId>>;
	}, [nullableWidths, tableId]);

	const setWidths = useCallback(
		(newWidths: Widths) => {
			dispatch(
				tableColumns.actions.setWidths({
					uid: saveSlot,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					widths: newWidths!,
				}),
			);
		},
		[dispatch, saveSlot],
	);

	return useMemo(() => ({ widths, setWidths }), [widths, setWidths]);
}

// TODO: Should be add the required types
export const useTableSetting = (savaSlot: string, tableId: keyof Tables) => {
	const { columnIds, setColumnIds } = useVisibleColumns(savaSlot, tableId);
	const { sortMethod, setSortMethod } = useSort(savaSlot, tableId);
	const { widths, setWidths } = useWidths(savaSlot, tableId);
	const columns = useColumns(tableId);

	return useMemo(
		() => ({
			columnIds,
			sortMethod,
			widths,
			columns,
			tables,
			setColumnIds,
			setSortMethod,
			setWidths,
		}),
		[
			setColumnIds,
			setSortMethod,
			setWidths,
			columnIds,
			sortMethod,
			widths,
			columns,
		],
	);
};
